import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";

import {
  StyledTooltip,
  StyledTooltipContainer,
} from "styles/components/tooltip";

interface TooltipProps {
  text?: string;
  size?: "lg" | "auto";
  position?:
    | "left"
    | "right"
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right";
  className?: string;
  wrapClassName?: string;
  children?: ReactNode;
  content?: ReactNode;
  disabled?: boolean;

  getVisible?: (visible: boolean) => void;
}

const Tooltip: FC<TooltipProps> = ({
  children,
  text,
  size,
  position = "right",
  className,
  wrapClassName,
  disabled = false,
  content,
  getVisible,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [isVisible, setVisibility] = useState<boolean>(false);

  const wrapTooltipClassName = clsx(["tooltip-container", wrapClassName]);

  const tooltipClassName = clsx([
    `tooltip tooltip_${position} tooltip_${size}`,
    {
      tooltip_open: isVisible && !disabled,
    },
    className,
  ]);

  const renderedTooltip = useMemo(
    () =>
      isVisible &&
      (text || content) &&
      !disabled && (
        <StyledTooltip className={tooltipClassName}>
          <div className="tooltip-triangle" />
          {content || <p className="tooltip-text">{text}</p>}
        </StyledTooltip>
      ),
    [content, disabled, isVisible, text, tooltipClassName]
  );

  const handleMouseOver = useCallback(() => {
    if (disabled) return;

    getVisible?.(true);

    setVisibility(true);
  }, [disabled, getVisible]);

  const handleMouseOut = useCallback(() => {
    getVisible?.(false);

    setVisibility(false);
  }, [getVisible]);

  useEffect(() => {
    const node = ref.current;

    if (node) {
      node.addEventListener("mouseover", handleMouseOver);
      node.addEventListener("mouseout", handleMouseOut);

      return () => {
        node.removeEventListener("mouseover", handleMouseOver);
        node.removeEventListener("mouseout", handleMouseOut);
      };
    }
  }, [handleMouseOver, handleMouseOut]);

  return (
    <StyledTooltipContainer ref={ref} className={wrapTooltipClassName}>
      {children}
      {renderedTooltip}
    </StyledTooltipContainer>
  );
};

export default Tooltip;
